import { authFetch } from './fetchHelpers';
import {
  Transaction,
  Account,
  Tag,
  Page,
  EmailRecordSummary,
  EmailRecordDetail,
  CreditCardBillSummary,
  PortfolioSummary,
  PortfolioTrade,
  PortfolioTradeRequest,
} from '../types';

/**
 * Updates a transaction on the server.
 * @param id The ID of the transaction to update.
 * @param transactionData The partial transaction data to update (e.g., { notes: "New note" }).
 * @returns The updated transaction data from the server.
 * @throws Error if the update fails.
 */
export const updateTransaction = async (
  id: number,
  transactionData: Partial<Omit<Transaction, 'id'>> // Allow updating subset of fields, except id
): Promise<Transaction> => {
  const response = await authFetch(`/transactions/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(transactionData),
  });

  if (!response.ok) {
    // Attempt to read error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error("Failed to update transaction:", errorMessage);
    throw new Error(`Failed to update transaction: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Fetches all accounts from the server.
 * @returns An array of account objects.
 * @throws Error if the fetch fails.
 */
export const fetchAccounts = async (): Promise<Account[]> => {
  const response = await authFetch(`/accounts`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error("Failed to fetch accounts:", errorMessage);
    throw new Error(`Failed to fetch accounts: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Fetches a specific account by ID.
 * @param id The ID of the account to fetch.
 * @returns The account data.
 * @throws Error if the fetch fails.
 */
export const fetchAccountById = async (id: number): Promise<Account> => {
  const response = await authFetch(`/accounts/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to fetch account with ID ${id}:`, errorMessage);
    throw new Error(`Failed to fetch account: ${errorMessage}`);
  }

  return response.json();
};

export const fetchCreditCardBills = async (accountId: number): Promise<CreditCardBillSummary[]> => {
  const response = await authFetch(`/accounts/${accountId}/credit-card-bills`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to fetch credit card bills for account ${accountId}:`, errorMessage);
    throw new Error(`Failed to fetch credit card bills: ${errorMessage}`);
  }

  return response.json();
};

export const fetchPortfolioSummary = async (accountId: number): Promise<PortfolioSummary> => {
  const response = await authFetch(`/accounts/${accountId}/portfolio/summary`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to fetch portfolio summary for account ${accountId}:`, errorMessage);
    throw new Error(`Failed to fetch portfolio summary: ${errorMessage}`);
  }

  return response.json();
};

export const fetchPortfolioTrades = async (accountId: number): Promise<PortfolioTrade[]> => {
  const response = await authFetch(`/accounts/${accountId}/portfolio/trades`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to fetch portfolio trades for account ${accountId}:`, errorMessage);
    throw new Error(`Failed to fetch portfolio trades: ${errorMessage}`);
  }

  return response.json();
};

export const createPortfolioTrade = async (
  accountId: number,
  tradeData: PortfolioTradeRequest
): Promise<PortfolioTrade> => {
  const response = await authFetch(`/accounts/${accountId}/portfolio/trades`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(tradeData),
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to create portfolio trade for account ${accountId}:`, errorMessage);
    throw new Error(`Failed to create portfolio trade: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Creates a new account on the server.
 * @param accountData The data for the new account.
 * @returns The created account data from the server.
 * @throws Error if the creation fails.
 */
export const createAccount = async (accountData: Omit<Account, 'id' | 'createdAt' | 'updatedAt'>): Promise<Account> => {
  const response = await authFetch(`/accounts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(accountData),
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error("Failed to create account:", errorMessage);
    throw new Error(`Failed to create account: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Updates the balance of a specific account.
 * @param accountId The ID of the account to update.
 * @param newBalance The new balance amount.
 * @returns The updated account data from the server.
 * @throws Error if the update fails.
 */
export const updateAccountBalance = async (accountId: number, newBalance: number): Promise<Account> => {
  const response = await authFetch(`/accounts/${accountId}/balance`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ balance: newBalance }),
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to update account balance for account ${accountId}:`, errorMessage);
    throw new Error(`Failed to update account balance: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Creates a new transaction on the server.
 * @param transactionData The data for the new transaction.
 * @returns The created transaction data from the server.
 * @throws Error if the creation fails.
 */
export const createTransaction = async (transactionData: Partial<Omit<Transaction, 'id'>>): Promise<Transaction> => {
  // Prepare the payload for the backend.

  const response = await authFetch(`/transactions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    // Send the transformed payload
    body: JSON.stringify(transactionData),
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error("Failed to create transaction:", errorMessage);
    throw new Error(`Failed to create transaction: ${errorMessage}`);
  }

  return response.json();
};


/**
 * Fetches the supported account types and their suggested names.
 * @returns An array of objects, each containing an account type and name.
 * @throws Error if the fetch fails.
 */
export const getSupportedAccountInfo = async (): Promise<Record<string, string[]>> => {
  const response = await authFetch(`/accounts/supported`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error("Failed to fetch supported account info:", errorMessage);
    throw new Error(`Failed to fetch supported account info: ${errorMessage}`);
  }

  // API returns an object like:
  // {"Savings":["HDFC","ICICI"],"Debit Card":["HDFC","ICICI"],"Credit Card":["HDFC","ICICI"]}
  return response.json();
};

// Fetches a paginated list of transactions
export async function fetchTransactions(page: number, size: number): Promise<Page<Transaction>> {
  const response = await authFetch(`/transactions?page=${page}&size=${size}&sort=transactionDate,desc`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error("Failed to fetch transactions:", errorMessage);
    throw new Error(`Failed to fetch transactions: ${errorMessage}`);
  }
  return response.json();
}

export async function fetchEmails(page: number, size: number): Promise<Page<EmailRecordSummary>> {
  const response = await authFetch(`/emails?page=${page}&size=${size}&sort=receivedAt,desc`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error("Failed to fetch emails:", errorMessage);
    throw new Error(`Failed to fetch emails: ${errorMessage}`);
  }
  return response.json();
}

export async function fetchEmailById(id: number): Promise<EmailRecordDetail> {
  const response = await authFetch(`/emails/${id}`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error(`Failed to fetch email with ID ${id}:`, errorMessage);
    throw new Error(`Failed to fetch email: ${errorMessage}`);
  }
  return response.json();
}

export async function reprocessEmail(id: number): Promise<EmailRecordDetail> {
  const response = await authFetch(`/emails/${id}/reprocess`, { method: 'POST' });
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error(`Failed to reprocess email with ID ${id}:`, errorMessage);
    throw new Error(`Failed to reprocess email: ${errorMessage}`);
  }
  return response.json();
}

export async function fetchEmailByMessageId(messageId: string): Promise<EmailRecordSummary | null> {
  const response = await authFetch(`/emails/by-message-id?messageId=${encodeURIComponent(messageId)}`);
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error(`Failed to fetch email with message ID ${messageId}:`, errorMessage);
    throw new Error(`Failed to fetch source email: ${errorMessage}`);
  }
  return response.json();
}

// Fetches a paginated list of transactions for a specific account
export async function fetchTransactionsByAccountId(accountId: number, page: number = 0, size: number = 20): Promise<Page<Transaction>> {
  const response = await authFetch(`/transactions/account/${accountId}?page=${page}&size=${size}&sort=transactionDate,desc`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error("Failed to fetch transactions for account:", errorMessage);
    throw new Error(`Failed to fetch transactions for account: ${errorMessage}`);
  }
  return response.json();
}

// Fetches transactions specifically for the current month
export async function fetchCurrentMonthTransactions(): Promise<Transaction[]> {
  const response = await authFetch(`/transactions/current-month`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error("Failed to fetch current month transactions:", errorMessage);
    throw new Error(`Failed to fetch current month transactions: ${errorMessage}`);
  }
  return response.json();
}

// Fetches transactions for a specific month and year
export async function fetchTransactionsForMonth(year: number, month: number): Promise<Transaction[]> {
  // Fetch all transactions for a given month and year (without pagination)
  const response = await authFetch(`/transactions/month?year=${year}&month=${month}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch transactions for ${year}-${month}`);
  }
  return response.json();
}

/**
 * Fetches transactions for a specific range of months.
 * @param startYear The starting year.
 * @param startMonth The starting month (1-indexed).
 * @param endYear The ending year.
 * @param endMonth The ending month (1-indexed).
 * @returns An array of transactions within the specified range.
 * @throws Error if the fetch fails.
 */
export async function fetchTransactionsForRange(
  startYear: number,
  startMonth: number,
  endYear: number,
  endMonth: number
): Promise<Transaction[]> {
  const url = `/transactions/range?startYear=${startYear}&startMonth=${startMonth}&endYear=${endYear}&endMonth=${endMonth}`;
  const response = await authFetch(url);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore
    }
    throw new Error(`Failed to fetch transactions for range ${startYear}-${startMonth} to ${endYear}-${endMonth}: ${errorMessage}`);
  }
  return response.json();
}

export interface TransactionYearRange {
  startYear: number | null;
  endYear: number | null;
  years: number[];
}

export async function fetchTransactionYearRange(): Promise<TransactionYearRange> {
  const response = await authFetch(`/transactions/years-range`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore
    }
    throw new Error(`Failed to fetch transaction year range: ${errorMessage}`);
  }
  return response.json();
}

// Fetches all tags
export async function fetchTags(transactionType?: Transaction['type']): Promise<Tag[]> {
  const query = transactionType ? `?transactionType=${transactionType}` : '';
  const response = await authFetch(`/tags${query}`);
  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) { }
    console.error("Failed to fetch tags:", errorMessage);
    throw new Error(`Failed to fetch tags: ${errorMessage}`);
  }
  return response.json();
}

export async function updateTransactionTagApi(
  transactionId: number,
  transactionData: Omit<Transaction, 'id' | 'tag'>,
  newTag: string | null
): Promise<void> {
  const response = await authFetch(`/transactions/${transactionId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ...transactionData,
      tag: newTag ?? '' // Empty string removes tag on backend
    }),
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error("Server error response:", errorBody);
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  console.log("Transaction tag updated successfully via API");
}

/**
 * Calls the backend endpoint to split a transaction.
 * @param parentId The ID of the transaction to split.
 * @param amount1 The amount for the new sub-transaction.
 * @param amount2 The amount the parent transaction should be updated to.
 * @returns The updated parent transaction from the server.
 * @throws Error if the split fails.
 */
export const splitTransactionApi = async (
  parentId: number,
  amount1: number,
  amount2: number,
  tag?: string | null
): Promise<Transaction> => {
  const response = await authFetch(`/transactions/${parentId}/split`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ amount1, amount2, tag }), // Send the split details in the request body
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      // Try to parse backend error message
      const errorBody = await response.json();
      errorMessage = errorBody.message || errorBody.error || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // If body parsing fails, use the status text
      errorMessage = response.statusText || errorMessage;
    }
    console.error(`Failed to split transaction ${parentId}:`, errorMessage);
    // Throw the specific message from backend if available
    throw new Error(`Failed to split transaction: ${errorMessage}`);
  }

  return response.json(); // Return the updated parent transaction
};

/**
 * Fetches a specific transaction by ID.
 * @param id The ID of the transaction to fetch.
 * @returns The transaction data.
 * @throws Error if the fetch fails.
 */
export const fetchTransactionById = async (id: number): Promise<Transaction> => {
  const response = await authFetch(`/transactions/${id}`);

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore
    }
    console.error(`Failed to fetch transaction with ID ${id}:`, errorMessage);
    throw new Error(`Failed to fetch transaction: ${errorMessage}`);
  }

  return response.json();
};

/**
 * Deletes a transaction by its ID.
 * @param id The ID of the transaction to delete.
 * @throws Error if the delete operation fails.
 */
export const deleteTransaction = async (id: number): Promise<void> => {
  const response = await authFetch(`/transactions/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    // Attempt to read error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to delete transaction with ID ${id}:`, errorMessage);
    throw new Error(`Failed to delete transaction: ${errorMessage}`);
  }

  // No content expected on successful delete
};

/**
 * Deletes an account on the server.
 * @param id The ID of the account to delete.
 * @returns Promise that resolves when the deletion is successful.
 * @throws Error if the deletion fails.
 */
export const deleteAccount = async (id: string): Promise<void> => {
  const response = await authFetch(`/accounts/${id}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      // Try to parse backend error message if available
      const errorBody = await response.json();
      errorMessage = errorBody.message || errorBody.error || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to delete account with ID ${id}:`, errorMessage);
    throw new Error(`Failed to delete account: ${errorMessage}`);
  }

  // No content expected on successful DELETE, so just return void
  return;
};

/**
 * Merges a child transaction back into its parent.
 * @param childId The ID of the child transaction to merge.
 * @returns The updated parent transaction data.
 * @throws Error if the merge fails.
 */
export const mergeTransactionApi = async (
  childId: number
): Promise<Transaction> => {
  const response = await authFetch(`/transactions/${childId}/merge`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // No body needed for this request
    },
  });

  if (!response.ok) {
    // Attempt to read error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`;
    try {
      const errorBody = await response.json();
      errorMessage = errorBody.message || JSON.stringify(errorBody) || errorMessage;
    } catch (e) {
      // Ignore if response body is not JSON or empty
    }
    console.error(`Failed to merge transaction ${childId}:`, errorMessage);
    throw new Error(`Failed to merge transaction: ${errorMessage}`);
  }

  return response.json();
};
